/**
 * NorthstarWelcome — the awakening sequence.
 *
 * Gated by `activated == true` (READ-ONLY check). This component MUST
 * NOT contain any code path that can write to `activated_at`,
 * connection status, matching state, or intake state. It only:
 *   1. Reads /api/northstar/activation and /api/northstar/profile
 *   2. Reveals the 10 orbs sequentially
 *   3. POSTs /api/northstar/awakening/seen when the sequence completes
 *      — that endpoint is server-side hardened to only write
 *      `awakening_seen_at`.
 *
 * Reduced motion honored: the sequence collapses to a calm static
 * reveal with no swoops.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { useActivation } from "../../hooks/useActivation";
import { api } from "../../lib/auth";
import DeepSpaceBackground from "../../components/northstar/DeepSpaceBackground";
import IdentityCenter from "../../components/northstar/IdentityCenter";
import OrbCanvas from "../../components/northstar/OrbCanvas";
import { ORBS } from "../../constants/northstarOrbs";

const REVEAL_STEP_MS = 350;
const AWAKENING_ROUTE = "/northstar/welcome";

export default function NorthstarWelcome() {
  const { data: activation, loading } = useActivation();
  const [profile, setProfile] = useState(null);
  const [reveal, setReveal] = useState(0);
  const [done, setDone] = useState(false);
  const [markingErr, setMarkingErr] = useState(null);
  const navigate = useNavigate();
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    api
      .get("/northstar/profile")
      .then((r) => setProfile(r.data))
      .catch(() => setProfile(null));
  }, []);

  // Sequential orb reveal
  useEffect(() => {
    if (!activation || activation.status !== "activated") return;
    if (reveal >= ORBS.length) return;
    const step = reducedMotion ? 120 : REVEAL_STEP_MS;
    const t = setTimeout(() => setReveal((r) => r + 1), step);
    return () => clearTimeout(t);
  }, [reveal, activation, reducedMotion]);

  // Mark seen once revealed
  useEffect(() => {
    if (reveal >= ORBS.length && !done) {
      setDone(true);
      api.post("/northstar/awakening/seen").catch((e) => setMarkingErr(e));
    }
  }, [reveal, done]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#040816]">
        <Heart className="w-8 h-8 text-[#7ED493] animate-pulse" />
      </div>
    );
  }
  if (!activation) return <Navigate to="/portal" replace />;
  if (activation.status !== "activated") {
    if (activation.should_render_holding_view) return <Navigate to="/portal/holding" replace />;
    // Phase 4 (Option B): a patient with intake-based Northstar access
    // (`northstar_access_granted === true` but `status !== "activated"`)
    // should NOT be sent back to /portal — they have Northstar access.
    // Only the Buddy-specific awakening ceremony is skipped; they
    // continue straight to the real shell.
    if (activation.northstar_access_granted) return <Navigate to="/northstar" replace />;
    return <Navigate to="/portal" replace />;
  }

  return (
    <div className="min-h-screen text-white relative bg-[#040816]" data-testid="northstar-welcome">
      <DeepSpaceBackground />

      <div className="relative z-10 flex flex-col items-center px-4 pt-14 pb-16">
        <p className="text-xs uppercase tracking-[0.28em] text-[#7ED493] mb-3" data-testid="welcome-eyebrow">
          Northstar
        </p>
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-center max-w-2xl leading-tight"
          data-testid="welcome-headline"
        >
          You&rsquo;re connected. Now let&rsquo;s build your Northstar.
        </h1>

        <div className="mt-4 relative w-full" style={{ minHeight: "60vh" }}>
          <div className="fixed inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-screen h-screen pointer-events-auto">
              <OrbCanvas reveal={reveal} centerSize={140} />
            </div>
          </div>
          <div
            className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none"
            style={{ zIndex: 50 }}
          >
            <div className="pointer-events-auto flex flex-col items-center">
              <IdentityCenter profile={profile} size={140} />
            </div>
          </div>
        </div>

        <div
          className={`mt-8 text-center transition-opacity duration-500 ${done ? "opacity-100" : "opacity-0"}`}
          data-testid="welcome-closing"
          aria-hidden={!done}
        >
          <p className="text-lg text-white/90 max-w-xl">
            We&rsquo;ve got you &mdash; every step of the way.
          </p>
          <button
            onClick={() => navigate("/northstar")}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#7ED493] text-[#0B132B] font-semibold hover:bg-white transition-colors"
            data-testid="welcome-enter-northstar"
          >
            Enter Northstar &rarr;
          </button>
          {markingErr && (
            <p className="mt-3 text-xs text-white/50" data-testid="welcome-mark-error">
              (We couldn&rsquo;t record this sequence — you can revisit it any time from Profile → Settings.)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export { AWAKENING_ROUTE };
